// A feedback report as the reporter builds it (a draft), and the body
// `POST /api/feedback` takes. The screenshot and the logs are opt-in: they
// are sent only while ticked, whatever was captured.
import type { LogLine } from './logs';

/** The element the reporter picked. */
export interface PickedElement {
  selector: string;
  text: string;
}

export interface Draft {
  note: string;
  /** The app's path and query. */
  route: string;
  /** `png`: the screenshot as it will be sent (annotations and blur applied), base64; null until captured. */
  screenshot: { attached: boolean; png: string | null };
  logs: { attached: boolean; lines: LogLine[] };
  element: PickedElement | null;
}

/** The body of `POST /api/feedback`. */
export interface ReportBody {
  note: string;
  route: string;
  client_version: string;
  browser: string;
  screenshot?: string;
  logs?: LogLine[];
  element?: PickedElement;
}

/** The server's limits (crates/server/src/api/feedback.rs). */
export const LIMITS = { note: 10_000, route: 2048, clientVersion: 200, browser: 512 } as const;

/** `s` on one line, at most `max` UTF-8 bytes. */
function fit(s: string, max: number): string {
  const one = s.replace(/[\u0000-\u001f\u007f]+/g, ' ');
  const enc = new TextEncoder();
  if (enc.encode(one).length <= max) return one;
  let out = '';
  for (const ch of one) {
    if (enc.encode(out + ch).length > max) break;
    out += ch;
  }
  return out;
}

/** The body for `draft`, with only what the reporter ticked. */
export function buildReport(draft: Draft, env: { clientVersion: string; browser: string }): ReportBody {
  const body: ReportBody = {
    note: draft.note.trim(),
    route: fit(draft.route, LIMITS.route),
    client_version: fit(env.clientVersion, LIMITS.clientVersion),
    browser: fit(env.browser, LIMITS.browser),
  };
  if (draft.screenshot.attached && draft.screenshot.png) body.screenshot = draft.screenshot.png;
  if (draft.logs.attached && draft.logs.lines.length) body.logs = draft.logs.lines;
  if (draft.element) body.element = draft.element;
  return body;
}

/** The client build: its entry script's name (hashed per build) and the engine's build. */
export function clientVersion(entry: string | null, engine: string): string {
  const name = entry ? (entry.split('/').pop() ?? entry) : 'dev';
  return `${name} engine-${engine}`;
}
